import Vue from 'vue';

/**
 * Rows just added on this page, shown before the list that owns them can list
 * them itself.
 *
 * The Postgres-backed tables (the company's Teams, a Team's members, the
 * Directory, Boards) trail Firestore by the mirror's lag, so a Team, a member, an
 * invitation or a board created a moment ago is not in their next page yet. The
 * code that creates one notes it here with its id and a stand-in row; the table
 * it belongs to (JustAddedRowsMixin) pins it on top of its first page, marked as
 * just added, and binds it live like any other row. The listing's own copy is
 * hidden while it is pinned, so it never shows twice.
 *
 * Held for the session, per list, so it survives the page going away and coming
 * back (a board is created, its page opens, the Boards list comes back). Cleared
 * when the list is refreshed, or its search or filters change: the listing then
 * stands alone.
 */

export interface JustAddedEntry<Row = unknown> {
	id: string;
	// A stand-in shaped like the list's own rows, until the row binds its document
	row: Row;
}

export interface JustAddedBucket {
	// Newest first
	entries: JustAddedEntry[];
	// The list's search and filters when it first showed these, or null until it has
	filtersKey: string | null;
}

const state = Vue.observable({
	buckets: {} as Record<string, JustAddedBucket>,
});

/** Which list a row belongs to. One key per list, so a row shows only in its own. */
export const JUST_ADDED_LIST = {
	companyTeams: (companyId: string): string => `company-teams/${companyId}`,
	teamMembers: (teamId: string): string => `team-members/${teamId}`,
	companyMembers: (companyId: string): string => `company-members/${companyId}`,
	teamBoards: (teamId: string): string => `team-boards/${teamId}`,
	personalBoards: (userId: string): string => `personal-boards/${userId}`,
};

export function justAddedBucket(listKey: string | null | undefined): JustAddedBucket | null {
	return (listKey && state.buckets[listKey]) || null;
}

/** Pin these on top of their list, the batch in its own order above anything added before. */
export function noteJustAdded<Row>(listKey: string | null | undefined, added: JustAddedEntry<Row>[]): void {
	if (!listKey || !added.length) return;
	const bucket: JustAddedBucket | null = justAddedBucket(listKey);
	const addedIds: Record<string, true> = {};
	const batch: JustAddedEntry[] = [];
	added.forEach((entry: JustAddedEntry<Row>) => {
		if (!entry.id || addedIds[entry.id]) return;
		addedIds[entry.id] = true;
		batch.push(entry);
	});
	const kept: JustAddedEntry[] = bucket ? bucket.entries.filter((entry: JustAddedEntry) => !addedIds[entry.id]) : [];
	Vue.set(state.buckets, listKey, {
		entries: [...batch, ...kept],
		filtersKey: bucket ? bucket.filtersKey : null,
	});
}

/** Remember the search and filters a list first showed these under, once. */
export function stampJustAdded(listKey: string | null | undefined, filtersKey: string): void {
	const bucket: JustAddedBucket | null = justAddedBucket(listKey);
	if (!bucket || bucket.filtersKey !== null) return;
	Vue.set(state.buckets, listKey as string, { ...bucket, filtersKey });
}

/** The list stands alone again: refreshed, or searched or filtered differently. */
export function forgetJustAdded(listKey: string | null | undefined): void {
	if (!listKey || !state.buckets[listKey]) return;
	Vue.delete(state.buckets, listKey);
}

/** One row is gone (deleted): it is not pinned any more. */
export function forgetJustAddedRow(listKey: string | null | undefined, id: string): void {
	const bucket: JustAddedBucket | null = justAddedBucket(listKey);
	if (!bucket) return;
	const entries: JustAddedEntry[] = bucket.entries.filter((entry: JustAddedEntry) => entry.id !== id);
	if (entries.length === bucket.entries.length) return;
	if (!entries.length) {
		Vue.delete(state.buckets, listKey as string);
		return;
	}
	Vue.set(state.buckets, listKey as string, { ...bucket, entries });
}

/**
 * The rows a list shows: the just-added on top, then the listing's own rows
 * without their copies of them, so nothing shows twice.
 */
export function mergeJustAddedRows<Row>(justAdded: Row[], listed: Row[], idOf: (row: Row) => string): Row[] {
	if (!justAdded.length) return listed;
	const pinned: Record<string, true> = {};
	justAdded.forEach((row: Row) => {
		pinned[idOf(row)] = true;
	});
	return [...justAdded, ...listed.filter((row: Row) => !pinned[idOf(row)])];
}

/** For tests: every list starts empty. */
export function forgetEveryJustAdded(): void {
	Object.keys(state.buckets).forEach((listKey: string) => Vue.delete(state.buckets, listKey));
}
